import React, { useState, useLayoutEffect } from 'react';
import { TouchableOpacity, Image, Linking, Platform } from 'react-native';
import styled, { css } from '@emotion/native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Feather } from '@expo/vector-icons';
import { useMutation } from '@apollo/client';
import { BodyText } from '@/shared/components/StyledText';
import Button from '@/shared/components/Button';
import useConfigStore from '@/stores/useConfigStore';
import useStore from '@/stores/useStore';
import SentryService from '@/services/sentry/Sentry.service';
import { sand, green, red } from '@/shared/styles/colors';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { LinkText, Link } from '../styledComponents';
import SectionLayout from '../SectionLayout';
import { IsDesktopProps } from '@/shared/styles/types';
import useAvailabilityStore from '@/stores/useAvailabilityStore';
import { ROUTES } from '@/shared/constants/routes';
import { DISCONNECT_CALENDAR } from '@/shared/mutations';
import { GET_STYLIST_SCHEDULING_DATA } from '@/shared/queries/stylistSchema';

const ConnectCalendar = () => {
  const router = useRouter();
  const { isDesktop } = useBreakpoint();
  const [loading, setLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);

  const environment = useConfigStore((state) => state.environment);
  const { setSnackbarState } = useStore();
  const { nylasConnections, disconnectCalendar } = useAvailabilityStore();

  const { connected } = useLocalSearchParams<{ connected: string }>();

  const [disconnectCalendarMutation] = useMutation(DISCONNECT_CALENDAR);

  const showCalendarConnectionError = () => {
    setSnackbarState({
      message: 'There was an issue connecting your calendar. Please try again.',
      severity: 'error',
      open: true,
    });
  };

  const showCalendarDisconnectError = () => {
    setSnackbarState({
      message: 'Failed to disconnect calendar. Please try again.',
      severity: 'error',
      open: true,
    });
  };

  const showCalendarDisconnectSuccess = () => {
    setSnackbarState({
      message: 'Calendar disconnected successfully',
      severity: 'success',
      open: true,
    });
  };

  useLayoutEffect(() => {
    // Only show error if connection failed and we don't have existing connections
    if (connected === 'false' && !nylasConnections?.[0]) {
      showCalendarConnectionError();
    }
  }, [connected, nylasConnections, setSnackbarState]);

  const connectCalendar = async () => {
    const stylistId = useAvailabilityStore.getState().stylistId;
    if (!stylistId) {
      showCalendarConnectionError();
      return;
    }
    setLoading(true);
    try {
      const authUrl = `${environment.server.baseUrl}/api/nylas/auth?stylistId=${stylistId}`;
      if (Platform.OS === 'web') {
        (window as { location: { href: string } }).location.href = authUrl;
      } else {
        const canOpen = await Linking.canOpenURL(authUrl);
        if (canOpen) {
          await Linking.openURL(authUrl);
        } else {
          showCalendarConnectionError();
        }
      }
    } catch (error) {
      SentryService.captureError('Error starting OAuth:', error);
      showCalendarConnectionError();
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnectLoading(true);
    try {
      const response = await disconnectCalendarMutation({
        refetchQueries: [{ query: GET_STYLIST_SCHEDULING_DATA }],
      });

      if (response.data?.disconnectCalendar?.success) {
        disconnectCalendar();
        showCalendarDisconnectSuccess();
      } else {
        showCalendarDisconnectError();
      }
    } catch (error) {
      SentryService.captureError('Error disconnecting calendar:', error);
      showCalendarDisconnectError();
    } finally {
      setDisconnectLoading(false);
    }
  };

  const handleStylingSessionsLinkPress = () => {
    router.push(ROUTES.HELP.path);
  };

  const footerLinks = (
    <LinkContent>
      <LinkText>Learn more about </LinkText>
      <TouchableOpacity
        onPress={handleStylingSessionsLinkPress}
        accessibilityRole="link"
      >
        <Link>Styling Sessions</Link>
      </TouchableOpacity>
      <LinkText>.</LinkText>
    </LinkContent>
  );

  return (
    <SectionLayout
      image={
        <Image
          source={require('@/assets/images/InstantStylingClientIntro.png')}
          style={{ width: 257, height: 172, borderRadius: 16 }}
          resizeMode="cover"
        />
      }
      heading="Let's make things official"
      description="Sync your go-to calendar so we know when you're free or busy. No double bookings. No chaos."
      buttonText={!nylasConnections?.[0] ? 'Sync Calendar(s)' : ''}
      isButtonDisabled={loading}
      onButtonPress={connectCalendar}
      footer={footerLinks}
      descriptionStyle={{ maxWidth: isDesktop ? 460 : 280 }}
      titleStyle={{ maxWidth: isDesktop ? '100%' : 258 }}
    >
      {nylasConnections?.[0] && (
        <ConnectedContainer isDesktop={isDesktop}>
          <Feather
            name="check"
            size={16}
            color={green}
            style={{ marginRight: 5 }}
          />
          <ConnectedText isDesktop={isDesktop}>
            Calendar ({nylasConnections?.[0]?.email}) Connected!
          </ConnectedText>
        </ConnectedContainer>
      )}
      {nylasConnections?.[0] && (
        <DisconnectButtonContainer isDesktop={isDesktop}>
          <Button
            text="Disconnect"
            onPress={handleDisconnect}
            disabled={disconnectLoading}
            loading={disconnectLoading}
            customStyle={disconnectButtonStyles}
            customLabelStyle={disconnectButtonLabelStyles}
          />
        </DisconnectButtonContainer>
      )}
    </SectionLayout>
  );
};

const ConnectedText = styled(BodyText)<IsDesktopProps>(({ isDesktop }) => ({
  textAlign: 'center',
  color: green,
  fontWeight: 'bold',
  fontSize: isDesktop ? 16 : 14,
}));

const ConnectedContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  marginTop: isDesktop ? 32 : 24,
  paddingVertical: 10,
  paddingHorizontal: isDesktop ? 40 : 32,
  backgroundColor: sand,
  flexDirection: 'row',
  alignItems: 'center',
}));

const DisconnectButtonContainer = styled.View<IsDesktopProps>(
  ({ isDesktop }) => ({
    marginTop: isDesktop ? 28 : 12,
    alignItems: 'center',
    width: '100%',
    maxWidth: 302,
  }),
);

const LinkContent = styled.View({
  marginTop: 24,
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  paddingHorizontal: 45,
});

const disconnectButtonStyles = css({
  height: 40,
  width: 'auto',
  paddingHorizontal: 40,
});

const disconnectButtonLabelStyles = css({
  fontWeight: '600',
  fontSize: 10,
});

export default ConnectCalendar;
